import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  PackageCheck,
  Receipt,
  RefreshCw,
  ShoppingBag,
  Truck,
  Wallet,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Meta from "../../components/Meta";

import {
  getMyOrders,
  resetOrderError,
  resetOrderMeta,
} from "../../actions/orderActions";

import {
  selectMyOrders,
  selectMyOrdersState,
} from "../../selectors/orderSelectors";
import {
  getFulfillmentStatusLabel as getSharedFulfillmentStatusLabel,
  getFulfillmentStatusTone,
  getOrderStatusLabel as getSharedOrderStatusLabel,
  getOrderStatusTone,
  getPaymentStatusLabel as getSharedPaymentStatusLabel,
  getPaymentStatusTone,
} from "./orderViewUtils";

import "./OrderHistoryScreen.css";
import OrderTimeline from "./OrderTimeline";
import CustomerCheckoutRecovery from "./CustomerCheckoutRecovery";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeLower(value = "") {
  return safeText(value).toLowerCase();
}

function roundMoney(value = 0) {
  const number = Number(value || 0);

  if (!Number.isFinite(number)) return 0;

  return Math.round(number * 100) / 100;
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safeText(currency) || "GBP",
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function getOrderCurrency(order = {}) {
  return safeText(order?.currency || order?.totals?.currency || "GBP");
}

function getOrderGrandTotal(order = {}) {
  const direct = Number(
    order?.grandTotal ?? order?.totals?.grandTotal ?? order?.total,
  );

  if (Number.isFinite(direct)) {
    return roundMoney(direct);
  }

  const subtotal = Number(order?.subtotal ?? order?.totals?.subtotal ?? 0);
  const shippingTotal = Number(
    order?.shippingTotal ?? order?.totals?.shippingTotal ?? 0,
  );
  const taxTotal = Number(order?.taxTotal ?? order?.totals?.taxTotal ?? 0);
  const discountTotal = Number(
    order?.discountTotal ?? order?.totals?.discountTotal ?? 0,
  );

  return roundMoney(subtotal + shippingTotal + taxTotal - discountTotal);
}

function getOrderPlacedAt(order = {}) {
  return (
    order?.paidAt ||
    order?.payment?.paidAt ||
    order?.createdAt ||
    order?.placedAt ||
    ""
  );
}

function getDeliveryLabel(order = {}) {
  const method = normalizeLower(
    order?.fulfillment?.method || order?.fulfillmentMethod ||
      order?.delivery?.method ||
      order?.shippingMethod ||
      "",
  );

  if (method === "pickup") return "Pickup";
  if (method === "delivery") return "Delivery";

  return method ? method.replace(/_/g, " ") : "Delivery";
}

function getPaymentMethodLabel(order = {}) {
  const provider = normalizeLower(
    order?.payment?.provider ||
      order?.payment?.paymentProvider ||
      order?.payment?.method ||
      order?.payment?.paymentMethod,
  );

  if (provider === "paypal") return "PayPal";
  if (provider === "sumup") return "SumUp";
  if (provider === "stripe") return "Card";

  return provider ? provider : "Payment";
}

function getItemCount(order = {}) {
  if (Number.isFinite(Number(order?.itemCount))) {
    return Math.max(0, Math.floor(Number(order.itemCount)));
  }

  if (Array.isArray(order?.items)) {
    return order.items.reduce(
      (sum, item) => sum + Math.max(0, Number(item?.qty || 0)),
      0,
    );
  }

  return 0;
}

function getLineItemsPreview(order = {}) {
  const items = Array.isArray(order?.items) ? order.items : [];

  return items
    .slice(0, 3)
    .map((item) => {
      const qty = Number(item?.qty || 0);
      const name = safeText(item?.productName || item?.name || "Item");

      return qty > 1 ? `${name} x ${qty}` : name;
    })
    .filter(Boolean)
    .join(" · ");
}

function getOrderReference(order = {}) {
  return safeText(order?.orderNumber || order?._id || order?.id || "");
}

/* -------------------------------------------------------------------------- */
/*                                  Pieces                                    */
/* -------------------------------------------------------------------------- */

function EmptyOrdersState() {
  return (
    <section className="order-history-screen__empty">
      <div className="order-history-screen__empty-icon">
        <ShoppingBag size={28} />
      </div>

      <h1>Your orders</h1>

      <p>
        You have not placed any orders yet. When you complete checkout, your
        orders will appear here.
      </p>

      <Link to="/" className="order-history-screen__primary-button">
        <span>Continue shopping</span>
        <ArrowRight size={16} />
      </Link>
    </section>
  );
}

function OrdersSkeleton() {
  return (
    <main className="order-history-screen order-history-screen--state">
      <Meta title="My orders | Academy Costumes London" robots="noindex, nofollow" />

      <section className="order-history-screen__state-card">
        <Loader2 size={22} className="order-history-screen__spin" />
        <p>Loading your orders…</p>
      </section>
    </main>
  );
}

function OrderStatusPill({ tone = "neutral", label = "" }) {
  return (
    <span
      className={`order-history-screen__status-pill order-history-screen__status-pill--${tone}`}
    >
      {label}
    </span>
  );
}

function OrderCard({ order }) {
  const orderNumber = getOrderReference(order);
  const currency = getOrderCurrency(order);
  const total = getOrderGrandTotal(order);
  const placedAt = formatDate(getOrderPlacedAt(order));
  const orderStatus = getSharedOrderStatusLabel(order);
  const paymentStatus = getSharedPaymentStatusLabel(order);
  const deliveryLabel = getDeliveryLabel(order);
  const fulfillmentStatus = getSharedFulfillmentStatusLabel(order);
  const paymentMethodLabel = getPaymentMethodLabel(order);
  const itemCount = getItemCount(order);
  const itemPreview = getLineItemsPreview(order);
  const tone = getOrderStatusTone(order);
  const paymentTone = getPaymentStatusTone(order);
  const fulfillmentTone = getFulfillmentStatusTone(order);

  return (
    <article className="order-history-screen__card">
      <div className="order-history-screen__card-head">
        <div>
          <strong>Order {orderNumber || "Reference unavailable"}</strong>
          {placedAt ? <p>Placed {placedAt}</p> : null}
        </div>

        <div className="order-history-screen__status-stack">
          <OrderStatusPill tone={tone} label={orderStatus} />
          <OrderStatusPill tone={paymentTone} label={paymentStatus} />
        </div>
      </div>

      {itemPreview ? (
        <p className="order-history-screen__item-preview">{itemPreview}</p>
      ) : null}
      <OrderTimeline order={order} />

      <div className="order-history-screen__card-grid">
        <div className="order-history-screen__meta">
          <PackageCheck size={16} />
          <div>
            <span>Items</span>
            <strong>{itemCount}</strong>
          </div>
        </div>

        <div className="order-history-screen__meta">
          {paymentMethodLabel === "PayPal" ? (
            <Wallet size={16} />
          ) : (
            <Receipt size={16} />
          )}
          <div>
            <span>Payment</span>
            <strong>
              {paymentMethodLabel} · {paymentStatus}
            </strong>
          </div>
        </div>

        <div className="order-history-screen__meta">
          <Truck size={16} />
          <div>
            <span>Fulfilment</span>
            <strong>{deliveryLabel}</strong>
            <OrderStatusPill tone={fulfillmentTone} label={fulfillmentStatus} />
          </div>
        </div>

        <div className="order-history-screen__meta">
          <ShoppingBag size={16} />
          <div>
            <span>Total</span>
            <strong>{formatMoney(total, currency)}</strong>
          </div>
        </div>
      </div>

      <div className="order-history-screen__card-actions">
        <Link
          to={`/order/${encodeURIComponent(orderNumber)}`}
          className="order-history-screen__primary-button"
        >
          <span>View order</span>
          <ArrowRight size={16} />
        </Link>

        <Link
          to={`/order/${encodeURIComponent(orderNumber)}/receipt`}
          className="order-history-screen__secondary-button"
        >
          <Receipt size={16} />
          <span>Receipt</span>
        </Link>
      </div>
    </article>
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Screen                                    */
/* -------------------------------------------------------------------------- */

export default function OrderHistoryScreen() {
  const dispatch = useDispatch();

  const listState = useSelector(selectMyOrdersState, shallowEqual);
  const orders = useSelector(selectMyOrders, shallowEqual);

  const [page, setPage] = useState(1);
  const [draftSearch, setDraftSearch] = useState("");
  const [draftStatus, setDraftStatus] = useState("");
  const [filters, setFilters] = useState({ search: "", status: "" });
  const lastErrorToastRef = useRef("");
  const pageSize = Number(listState?.limit || 20);
  const totalPages = Math.max(1, Number(listState?.pages || 1));
  const totalCount = Math.max(0, Number(listState?.count || 0));

  useEffect(() => {
    dispatch(
      getMyOrders({
        page,
        limit: pageSize,
        ...filters,
      }),
    ).catch(() => {
      // handled by error effect
    });
  }, [dispatch, page, pageSize, filters]);

  useEffect(() => {
    const errorMessage = safeText(listState?.error || "");

    if (!errorMessage || lastErrorToastRef.current === errorMessage) return;

    lastErrorToastRef.current = errorMessage;
    toast.error(errorMessage);
  }, [listState?.error]);

  useEffect(() => {
    return () => {
      dispatch(resetOrderError());
      dispatch(resetOrderMeta());
    };
  }, [dispatch]);

  const paginationLabel = useMemo(() => {
    if (!totalCount) return "No orders";

    const start = (page - 1) * pageSize + 1;
    const end = Math.min(totalCount, start + Math.max(0, orders.length - 1));

    return `Showing ${start}-${end} of ${totalCount}`;
  }, [orders.length, page, pageSize, totalCount]);

  const handleRefresh = useCallback(async () => {
    try {
      await dispatch(
        getMyOrders({
          page,
          limit: pageSize,
          ...filters,
        }),
      );
      toast.success("Orders refreshed.");
    } catch {
      // handled by error effect
    }
  }, [dispatch, page, pageSize, filters]);

  const handlePrevious = useCallback(() => {
    setPage((prev) => Math.max(1, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  if (listState?.loading && !orders.length) {
    return <OrdersSkeleton />;
  }

  return (
    <main className="order-history-screen">
      <Meta
        title="My orders | Academy Costumes London"
        description="Review your recent orders, payment status and receipts."
        robots="noindex, nofollow"
      />

      <nav
        className="order-history-screen__topbar"
        aria-label="Order history navigation"
      >
        <Link to="/" className="order-history-screen__back-link">
          <ArrowLeft size={18} />
          <span>Home</span>
        </Link>
      </nav>

      <header className="order-history-screen__header">
        <div>
          <p>Academy Costumes London</p>
          <h1>My orders</h1>
          <span>{paginationLabel}</span>
        </div>

        <button
          type="button"
          className="order-history-screen__secondary-button"
          onClick={handleRefresh}
          disabled={Boolean(listState?.loading)}
        >
          {listState?.loading ? (
            <Loader2 size={16} className="order-history-screen__spin" />
          ) : (
            <RefreshCw size={16} />
          )}
          <span>Refresh</span>
        </button>
      </header>

      <form className="order-filters" onSubmit={(event) => { event.preventDefault(); setPage(1); setFilters({ search: draftSearch.trim(), status: draftStatus }); }}>
        <label>Search orders<input type="search" value={draftSearch} maxLength={120} onChange={(event) => setDraftSearch(event.target.value)} placeholder="Order number or item" /></label>
        <label>Status<select value={draftStatus} onChange={(event) => setDraftStatus(event.target.value)}>
          <option value="">All orders</option>
          {["paid", "processing", "ready_to_ship", "ready_for_pickup", "shipped", "delivered", "fulfilled", "cancelled", "refund_pending", "partially_refunded", "refunded"].map((status) => <option key={status} value={status}>{getSharedOrderStatusLabel(status)}</option>)}
        </select></label>
        <button className="order-history-screen__secondary-button" type="submit" disabled={Boolean(listState.loading)}><Search size={16} /> Search</button>
      </form>

      <CustomerCheckoutRecovery />
      {!orders.length && (filters.search || filters.status) && !listState.error ? <p role="status">No orders match these filters.</p> : !orders.length && !listState.error ? (
        <EmptyOrdersState />
      ) : (
        <>
          <section className="order-history-screen__list">
            {orders.map((order) => (
              <OrderCard
                key={order?._id || order?.id || order?.orderNumber}
                order={order}
              />
            ))}
          </section>

          <footer className="order-history-screen__pagination">
            <button
              type="button"
              className="order-history-screen__secondary-button"
              onClick={handlePrevious}
              disabled={page <= 1 || Boolean(listState?.loading)}
            >
              Previous
            </button>

            <span>
              Page {page} of {totalPages}
            </span>

            <button
              type="button"
              className="order-history-screen__secondary-button"
              onClick={handleNext}
              disabled={page >= totalPages || Boolean(listState?.loading)}
            >
              Next
            </button>
          </footer>
        </>
      )}

      {!orders.length && safeText(listState?.error) ? (
        <section className="order-history-screen__notice">
          <AlertTriangle size={18} />
          <div>
            <strong>We could not load your orders.</strong>
            <p>{safeText(listState.error)}</p>
          </div>
        </section>
      ) : null}
    </main>
  );
}
